/**
 * 교대 편성 — 화면이 쓰는 순수 조각 (2026-08-30 · 판 K).
 *
 * ★ 달력 계산과 패턴 채우기를 화면에서 떼어 둔다. 「9월 3일이 몇 째 칸인가」 같은 것은 눈으로
 *   확인하기 어렵고, 한 칸 밀리면 **전원의 근무일이 하루씩 어긋난다.**
 */

export interface RosterDay {
  /** `YYYY-MM-DD` */
  date: string;
  day: number;
  /** 0=일 … 6=토. 주말을 다르게 그리는 데만 쓴다(휴무 여부는 편성이 정한다). */
  weekday: number;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** 그 달의 날짜 목록. */
export function monthDays(year: number, month1to12: number): RosterDay[] {
  const last = new Date(Date.UTC(year, month1to12, 0)).getUTCDate();
  const out: RosterDay[] = [];
  for (let d = 1; d <= last; d += 1) {
    out.push({
      date: `${year}-${pad(month1to12)}-${pad(d)}`,
      day: d,
      weekday: new Date(Date.UTC(year, month1to12 - 1, d)).getUTCDay(),
    });
  }
  return out;
}

/**
 * 패턴 채우기 — 조 순서를 **첫날부터 되풀이**한다(주주야야휴휴 같은 것).
 *
 * ★ 빈 칸(`null`)도 패턴의 한 자리다 — 「이 날은 안 정함」을 되풀이할 수 있어야 한다.
 * ★ `startIndex` 는 **사람마다 조를 어긋나게** 시작하기 위한 것이다. 교대는 조가 서로 밀려
 *   돌아가는 것이 본질이라, 이게 없으면 전원이 같은 날 같은 조가 된다.
 */
export function fillPattern(
  days: readonly RosterDay[],
  pattern: readonly (string | null)[],
  startIndex = 0,
): Record<string, string | null> {
  const out: Record<string, string | null> = {};
  if (pattern.length === 0) return out;
  days.forEach((d, i) => {
    const at = (i + startIndex) % pattern.length;
    out[d.date] = pattern[((at % pattern.length) + pattern.length) % pattern.length] ?? null;
  });
  return out;
}

/**
 * 바뀐 칸만 골라낸다 — 저장은 **고친 것만** 보낸다.
 *
 * ★ 한 달 전원을 통째로 보내면 2,000줄이 넘고, 안 건드린 사람의 편성까지 다시 쓴다.
 *   `null` 은 「지우기」라 **원래 있던 자리에서만** 뜻이 있다.
 */
export function changedItems(
  before: Readonly<Record<string, Record<string, string>>>,
  after: Readonly<Record<string, Record<string, string | null>>>,
): Array<{ userId: string; workDate: string; scheduleId: string | null }> {
  const out: Array<{ userId: string; workDate: string; scheduleId: string | null }> = [];
  for (const [userId, days] of Object.entries(after)) {
    const was = before[userId] ?? {};
    for (const [date, value] of Object.entries(days)) {
      const old = was[date] ?? null;
      if ((value ?? null) === old) continue;
      // 원래도 없고 지금도 없으면 보낼 것이 없다(위 비교가 이미 걸러 준다).
      out.push({ userId, workDate: date, scheduleId: value ?? null });
    }
  }
  return out;
}

/** 조마다 다른 색 — 이름이 아니라 **모양**으로 구별돼야 한 달 표가 읽힌다. */
export const SHIFT_TONES = ['a', 'b', 'c', 'd', 'e', 'f'] as const;
export function toneOf(index: number): string {
  return SHIFT_TONES[index % SHIFT_TONES.length] ?? SHIFT_TONES[0];
}


/**
 * ★★ **교대를 고르면 조가 «나와야» 한다** (2026-08-30 · 사용자 지적).
 *
 * 처음 판에서는 종류만 「교대근무」로 고르면 끝이고, 「주간」·「야간」·「휴무」는 **이름을 손으로
 * 쳐서 세 번 만들어야** 했다. 사용자가 「교대근무를 선택하여도 주간·야간으로 안 나온다」고 한
 * 것이 정확하다 — **고르는 순간 무엇이 생기는지 보이지 않으면 아무것도 안 생긴 것과 같다.**
 *
 * 그래서 세 조를 **기본값으로 미리 세워** 화면에 내보이고, 한 번에 만들 수 있게 한다.
 * 회사마다 시각은 다르므로 **고칠 수 있어야** 하고, 이미 있는 이름은 **다시 만들지 않는다**.
 */
export interface ShiftPreset {
  name: string;
  startTime: string;
  endTime: string;
  breakMinutes: number;
  workMinutes: number;
}

/** 2교대(주·야) + 휴무 — 가장 흔한 한 벌. 휴무는 **소정 0분**이라 지각을 묻지 않는다. */
export const DEFAULT_SHIFT_PRESETS: readonly ShiftPreset[] = [
  { name: '주간', startTime: '08:00', endTime: '17:00', breakMinutes: 60, workMinutes: 480 },
  { name: '야간', startTime: '22:00', endTime: '07:00', breakMinutes: 60, workMinutes: 480 },
  { name: '휴무', startTime: '00:00', endTime: '00:00', breakMinutes: 0, workMinutes: 0 },
];

/** 이미 있는 이름은 빼고 만들 것만 남긴다 — 두 번 눌러도 조가 겹치지 않는다. */
export function presetsToCreate(
  presets: readonly ShiftPreset[],
  existing: readonly { name: string; type: string }[],
): ShiftPreset[] {
  const have = new Set(existing.filter((s) => s.type === 'SHIFT').map((s) => s.name.trim()));
  return presets.filter((p) => p.name.trim().length > 0 && !have.has(p.name.trim()));
}

/** 자정을 넘는 조인가 — 화면이 «넘는다» 고 적어 줘야 오해가 없다. */
export function crossesMidnight(startTime: string, endTime: string): boolean {
  return endTime <= startTime && !(startTime === endTime && endTime === '00:00');
}
